import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';

import {
  catchError,
  debounceTime,
  delay,
  filter,
  Observable,
  of,
  startWith,
  Subscription,
  take,
  tap,
} from 'rxjs';
import { v4 } from 'uuid';

import {
  ConfirmationModalComponent,
  ConfirmationModalResult,
  GlobalErrorHandler,
  PageIdentifierService,
} from '@tablesready/common-ui';
import {
  CanvasComponent,
  CanvasObjectSelected,
  SHAPES_DIMENSIONS,
  FLOORPLAN_GLOBAL_CLASSNAME,
} from '@tablesready/canvas';
import { CanvasObjectsEnum, Shapes } from '@shared/core';
import { TableSizes } from '@shared/floor-plans';
import {
  FloorPlan,
  FloorPlanNewObject,
  FloorPlanNewSection,
  FloorPlanNewTable,
  FloorPlanObject,
  FloorPlanSection,
  FloorPlanTable,
  GroupedItems,
  ObjectAttributes,
  SectionAttributes,
  TableAttributes,
} from '@shared/floor-plan/models';
import { FloorPlanItemType } from '@shared/floor-plan/enums';
import { Resource } from '@shared/resource/models';
import { isOnFreePlan } from '@shared/user/functions';
import { SIZE_TO_TEXT } from './constants';
import {
  FloorPlanApiService,
  FloorPlanItemApiService,
  FloorPlanTableService,
  ResourceApiService,
  PricePlansRestrictionsService,
  PlanRestrictionsAlertService,
} from '../../../../../services';
import { PlansAlertData } from '../../../../../models';
import { FloorplanModalComponent, FloorPlanModalMode } from './modals';
import { FloorPlanTabs } from '../../enums';
import { CanvasObjectDrag } from './components';
import { FrontendModalService, FrontendModalSize } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { ThemeSwitchService } from '@tablesready/frontend-theme-switch';
import { WindowHeightService } from '@tablesready/frontend-nav-sidebar';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { FrontendBreakpointsService } from '@tablesready/frontend-breakpoints';

@Component({
  selector: 'tablesready-floor-plan',
  templateUrl: './floorplan-builder.component.html',
  styleUrls: ['./floorplan-builder.component.scss'],
  providers: [
    GlobalErrorHandler,
    FloorPlanTableService,
    PageIdentifierService,
  ],
})
export class FloorplanBuilderComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('graphCanvas', {
    static: true,
    read: CanvasComponent,
  }) canvas?: CanvasComponent;

  @ViewChild('graphCanvas', {
    static: true,
    read: ElementRef,
  }) canvasRef?: ElementRef;

  @ViewChild('popoutContent', {
    static: true,
    read: ElementRef,
  }) popoutEl?: ElementRef;

  leftLayoutOffsetPx = 0;
  tabs = [FloorPlanTabs.tables, FloorPlanTabs.sections];
  tabValues = FloorPlanTabs;
  openedAddTableForm = false;
  openedAddObjectForm = false;
  activeTab: FloorPlanTabs;
  enabledResources = false;
  isObjectEditMode = false;
  selectedObjectSectionId?: string;
  selectedFloorPlanId?: string;
  selectedSectionId?: string;
  isOnFreePlan = true;
  isOnBusinessPlan = false;
  trialCompleted?: boolean;
  tabsHeight = 64;

  rotation = 0;
  width = 0;
  height = 0;
  left = 0;
  top = 0;
  isFloorPlansLoading = true;
  isTable = false;
  selectedSize?: TableSizes;
  objectName?: string;
  selectedShape?: Shapes;
  selectedResourceId?: string;
  selectedObjectId?: string;
  lastSelectedObjectId?: string;
  allowSectionMultiSelection = false;
  tablesLoaded = false;
  generatedTableName?: string;
  isDraggingObject = false;
  multiTransformSelectMode = false;
  duplicatedTable = false;

  icon = AppIcon;
  rightSidebarBreakpoint = false;
  contentAreaHeight = window.innerHeight;
  iconSize = AppIconSize;
  iconColor = AppIconColor;
  popoutHidden = true;
  confirmChangesDialogOpen = false;
  locationId?: string;
  resources: Resource[];
  autoZoomed = false;
  plansAlertData: PlansAlertData;

  private floorPlanTablesCount = 0;
  private selectedCanvasObject?: CanvasObjectsEnum;
  private tableAddFormTouched = false;
  private objectAddFormTouched = false;
  private readonly subscriptions = new Subscription();

  constructor(
    readonly floorPlanTableService: FloorPlanTableService,
    private readonly cdr: ChangeDetectorRef,
    private readonly alertsService: AlertsService,
    private readonly windowHeightService: WindowHeightService,
    private readonly themeSwitch: ThemeSwitchService,
    private readonly breakpointService: FrontendBreakpointsService,
    private readonly pageIdentifyService: PageIdentifierService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly itemService: FloorPlanItemApiService,
    private readonly service: FloorPlanApiService,
    private readonly modalController: FrontendModalService,
    private readonly errorHandler: GlobalErrorHandler,
    private readonly resourceApiService: ResourceApiService,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private readonly planRestrictionsAlertService: PlanRestrictionsAlertService,
  ) {
    this.pageIdentifyService.setup(FLOORPLAN_GLOBAL_CLASSNAME);
  }

  ngAfterViewInit(): void {
    const layoutSub = this.breakpointService.observeFloorPlanRightSidebar()
      .subscribe((isMatched) => {
        this.popoutHidden = !this.rightSidebarBreakpoint;
        this.rightSidebarBreakpoint = isMatched;
      });
    this.subscriptions.add(layoutSub);

    const leftOffsetSub = this.breakpointService.mainNavSidebarWidth$
      .subscribe((value) => {
        this.leftLayoutOffsetPx = value;
      });
    this.subscriptions.add(leftOffsetSub);

    const HEIGHT_DEBOUNCE = 200;
    this.windowHeightService.init();
    const heightSub = this.windowHeightService.height$
      .pipe(
        startWith(window.innerHeight),
        debounceTime(HEIGHT_DEBOUNCE),
      )
      .subscribe(() => {
        this.cdr.detectChanges();

        setTimeout(() => {
          this.canvas.redrawScrollbars('FloorPlanBuilderComponent AfterViewInit() height sub');
          this.canvas.setCanvasDimensions();
        });
      });
    this.subscriptions.add(heightSub);
  }

  toggleMultiTransformMode(): void {
    this.canvas.deselectAllObjects();
    this.multiTransformSelectMode = !this.multiTransformSelectMode;
  }

  setTableFormTouched(tableFormTouched: boolean): void {
    this.tableAddFormTouched = tableFormTouched;
  }

  setObjectFormTouched(objectFormTouched: boolean): void {
    this.objectAddFormTouched = objectFormTouched;
  }

  getItems = (floorPlanId: string): Observable<GroupedItems> => this.floorPlanTableService.getItems(floorPlanId)
    .pipe(tap(({ tables }) => {
      this.floorPlanTablesCount = tables.length;
      this.tablesLoaded = true;
      if (this.autoZoomed) {
        return;
      }

      if (!this.autoZoomed && tables.length === 0) {
        this.autoZoomed = true;
        return;
      }

      setTimeout(() => {
        if (tables.length > 0) {
          this.canvas.autoZoom(tables);
          this.autoZoomed = true;
        }
      });
    }));

  onDeleteResource = (id: string): void => {
    this.resourceApiService.delete(id)
      .pipe(take(1))
      .subscribe();
  };

  onObjectDeleteClose = (id: string, floorPlanId: string): void => {
    this.itemService.deleteById(id)
      .pipe(take(1))
      .subscribe(() => {
        this.popoutHidden = true;
        this.hideForm();
        this.canvas.onFloorChange(floorPlanId, 'builder onObjectDeleteClose()');
      });
  };

  onTableDuplicate = (table: FloorPlanTable): void => {
    if (this.tableAddFormTouched) {
      this.alertsService.addWarning('you need to save or discard changes before duplicating this table.', 'Warning: ');

      return;
    }

    this.duplicatedTable = true;
    const resource = this.resources.find(resource => resource._id === table.resourceId);
    this.generatedTableName = `Table ${this.findAvailableTableName(this.floorPlanTablesCount + 1)}`;
    this.canvas.addTable(
      v4(),
      this.generatedTableName,
      `${resource.capacity.min}-${resource.capacity.max}`,
      '',
      table.width,
      table.height,
      this.top,
      this.left,
      table.shape,
      false,
      false,
      0,
      table.rotation,
      undefined,
      this.selectedObjectSectionId,
      true,
    );
  };

  onObjectSaveClose = (
    id: string,
    floorPlanId: string,
    objectName: string,
    attributes: ObjectAttributes,
  ): void => {
    const table: FloorPlanObject = {
      id,
      floorPlanId,
      type: FloorPlanItemType.object,
      name: String(objectName),
      ...attributes,
      getAttributes(): ObjectAttributes {
        return attributes;
      },
    };

    this.itemService.updateById(table)
      .pipe(take(1))
      .subscribe(() => {
        this.canvas.setObjectAsModifiedById(id);
        this.popoutHidden = true;
        this.hideForm();
        this.canvas.onFloorChange(floorPlanId, 'onObjectSaveClose() after updateById()');
      });
  };

  onTransformUpdate = (
    id: string,
    type: FloorPlanItemType.table | FloorPlanItemType.object,
    attributes: Partial<TableAttributes>,
    sectionId?: string,
  ): void => {
    this.itemService.updatePartialById(id, type, attributes)
      .pipe(
        take(1),
        tap(() => {
          if (type !== FloorPlanItemType.table) {
            return;
          }

          this.handleSectionUpdateForTable(id, sectionId, false, this.selectedObjectSectionId);
        }),
      )
      .subscribe();
  };

  onTableSaveClose = (
    id: string,
    floorPlanId: string,
    tableName: string,
    attributes: TableAttributes,
    sectionId?: string,
  ): void => {
    const table: FloorPlanTable = {
      id,
      floorPlanId,
      type: FloorPlanItemType.table,
      name: String(tableName),
      ...attributes,
      getAttributes(): TableAttributes {
        return attributes;
      },
    };

    this.itemService.updateById(table)
      .pipe(
        take(1),
        tap((savedItem) => {
          this.canvas.setObjectAsModifiedById(id);
          this.handleSectionUpdateForTable(id, sectionId, true, this.selectedObjectSectionId);
        }),
      )
      .subscribe();
  };

  sectionEditModeChange(sectionId?: string): void {
    this.allowSectionMultiSelection = sectionId !== undefined;
    this.selectedSectionId = sectionId;

    if (sectionId === undefined) {
      this.canvas.deselectAllObjects();
    }
  }

  handleSectionUpdateForTable(id: string, newSectionId: string, closeForm = true, oldSectionId?: string): void {
    let alreadyInSection: FloorPlanSection = undefined;
    let targetSection: FloorPlanSection = undefined;

    this.floorPlanTableService.savedSections.forEach((section) => {
      if (section.id === newSectionId) {
        targetSection = section;
      }

      if (section.tableIds.includes(id)) {
        alreadyInSection = section;
      }
    });

    const alreadyInTargetSection = targetSection?.id === alreadyInSection?.id;
    if (alreadyInTargetSection && !closeForm) {
      return;
    }

    if (alreadyInTargetSection) {
      this.afterSectionClose(true);
      return;
    }

    const saveTargetSection = (): void => {
      if (!targetSection) {
        this.afterSectionClose(true);
        return;
      }

      const { name, tableIds, color } = targetSection;
      this.onSectionCloseHandler(false, () => {
        this.afterSectionClose(true);
      })(name, [...tableIds, id], color, newSectionId);
    };

    if (alreadyInSection) {
      const { name, color } = alreadyInSection;
      const tableIds = alreadyInSection.tableIds.filter(tableId => tableId !== id);

      this.onSectionCloseHandler(false, () => {
        saveTargetSection();
      })(name, tableIds, color, oldSectionId);

      return;
    }

    saveTargetSection();
  }

  onSectionDelete = (id: string, callback: () => void): void => {
    this.itemService.deleteById(id)
      .pipe(take(1))
      .subscribe(() => {
        this.canvas.onFloorChange(this.selectedFloorPlanId, 'builder after section deleteById()', callback);
      });
  };

  floorChanged(id: string): void {
    if (id !== this.selectedFloorPlanId) {
      this.hideForm();
      this.selectedFloorPlanId = id;
    }
  }

  afterSectionClose = (popupHidden = false): void => {
    this.hideForm(popupHidden);
    this.setTableFormTouched(false);
    this.canvas.onFloorChange(this.selectedFloorPlanId, 'builder afterSectionClose()');
  };

  onTableFormClose = (shouldReload = false): void => {
    this.hideForm();
    this.setTableFormTouched(false);
    this.duplicatedTable = false;
    if (shouldReload) {
      this.canvas.onFloorChange(this.selectedFloorPlanId, 'builder onTableFormClose()');
    }
  };

  onObjectFormClosed = (shouldReload = false): void => {
    this.hideForm();
    this.setObjectFormTouched(false);
    if (shouldReload) {
      this.canvas.onFloorChange(this.selectedFloorPlanId, 'builder onObjectFormClosed()');
    }
  };

  onSectionClose(): (
    isAddingNewSection: boolean,
    callback: () => void
  ) => (
    name: string,
    tableIds: string[],
    color: string,
    sectionId?: string
  ) => void {
    return (isAddingNewSection: boolean, callback: () => void) => this.onSectionCloseHandler(
      isAddingNewSection,
      () => {
        this.afterSectionClose(true);

        if (callback) {
          callback();
        }
      },
    );
  }

  onObjectAddClose = (resourceName: string, attributes: ObjectAttributes): void => {
    const table: FloorPlanNewObject = {
      type: FloorPlanItemType.object,
      name: String(resourceName),
      ...attributes,
      getAttributes(): ObjectAttributes {
        return attributes;
      },
    };

    this.itemService.create(this.selectedFloorPlanId, table)
      .pipe(
        take(1),
        tap(() => {
          this.popoutHidden = true;
          this.hideForm();
        }),
        catchError((err) => {
          this.errorHandler.handleError(err);

          return of(null);
        }),
      )
      .subscribe((newObject) => {
        this.canvas.setObjectAsModifiedById(newObject.id);
        this.canvas.onFloorChange(this.selectedFloorPlanId, 'builder onObjectAddClose() after item creation');
      });
  };

  onTableCreateClose = (resourceName: string, attributes: TableAttributes, sectionId?: string): void => {
    const table: FloorPlanNewTable = {
      type: FloorPlanItemType.table,
      name: String(resourceName),
      ...attributes,
      getAttributes(): TableAttributes {
        return attributes;
      },
    };

    this.itemService.create(this.selectedFloorPlanId, table)
      .pipe(
        take(1),
        tap((createdTable) => {
          this.handleSectionUpdateForTable(createdTable.id, sectionId);

          const currentFloorIndex = this.floorPlanTableService.floorPlans
            .findIndex(item => item.id === this.selectedFloorPlanId);
          if (currentFloorIndex === -1) {
            return;
          }

          const newFloors = [...this.floorPlanTableService.floorPlans];
          newFloors[currentFloorIndex].tablesCount += 1;
          this.floorPlanTableService.floorPlans = newFloors;
        }),
        catchError((err) => {
          this.errorHandler.handleError(err);

          return of(null);
        }),
      )
      .subscribe();
  };

  onCreateResource = (newResource: Omit<Resource, '_id'>): Observable<Resource> => this.resourceApiService.create(newResource);

  onUpdateResource = (resource: Partial<Resource>): Observable<Resource> => this.resourceApiService.edit(resource);

  resourceChanged(data?: { _id: string; name: string }): void {
    if (!this.selectedObjectId || !data) {
      return;
    }

    this.canvas.setResourceId(this.selectedObjectId, data._id);
    this.canvas.setTableTextPropName('id', this.selectedObjectId, true, false, 0, data.name);
  }

  ngOnInit(): void {
    this.windowHeightService.init();
    this.windowHeightService.height$.subscribe((windowHeight) => {
      this.contentAreaHeight = windowHeight;
      this.canvas.redrawScrollbars('FloorPlanBuilderComponent ngOnInit() height sub');
    });

    this.activeTab = FloorPlanTabs.tables;

    const floorPlansSub = this.floorPlanTableService.floorPlans$
      .subscribe(() => {
        this.isFloorPlansLoading = false;
      });
    this.subscriptions.add(floorPlansSub);

    const themeSub = this.themeSwitch.themeChange$
      .pipe(delay(1))
      .subscribe(() => {
        this.hideForm(true);
      });
    this.subscriptions.add(themeSub);

    const resourcesStatusSub = this.authRestaurantService.select()
      .pipe(tap((user) => {
        this.enabledResources = user.location.resourceSettings.enabled;
      }))
      .subscribe();
    this.subscriptions.add(resourcesStatusSub);

    const locationSub = this.authRestaurantService.selectAndDetectLocationChanges()
      .pipe(tap((user) => {
        const { account, location } = user;
        this.locationId = location._id;
        this.isOnFreePlan = isOnFreePlan(user);
        this.isOnBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(account.subscriptionType, location.recurly);
        this.plansAlertData = this.planRestrictionsAlertService.getAlertData(
          account.subscriptionType,
          account.recurly,
          location.recurly,
        );
        this.trialCompleted = account.recurly?.trial;
      }))
      .subscribe();
    this.subscriptions.add(locationSub);

    const resourcesSub = this.floorPlanTableService.resources$
      .pipe(tap((resources) => {
        this.resources = resources;
      }))
      .subscribe();
    this.subscriptions.add(resourcesSub);
  }

  onPlanEdit(plan: FloorPlan): void {
    if (!this.isOnBusinessPlan) {
      return;
    }

    this.modalController.create<FloorplanModalComponent, unknown>(FloorplanModalComponent, {
      defaultChecked: this.floorPlansCount === 1,
      mode: FloorPlanModalMode.edit,
      item: plan,
      floorplans: this.floorPlanTableService.floorPlans,
      onClose: (mode, item, instance, deleteResources): void => {
        this.modalSave(mode, item, instance, deleteResources);
      },
    })
      .pipe(take(1))
      .subscribe();
  }

  addFloorPlan(): void {
    if (!this.enabledResources || !this.isOnBusinessPlan) {
      return;
    }

    this.modalController.create<FloorplanModalComponent, unknown>(FloorplanModalComponent, {
      defaultChecked: this.floorPlansCount === 0,
      floorplans: this.floorPlanTableService.floorPlans,
      onClose: (mode, item, instance): void => {
        this.modalSave(mode, item, instance);
      },
    })
      .pipe(take(1))
      .subscribe();
  }

  onCanvasClicked(): void {
    if (this.activeTab === FloorPlanTabs.sections) {
      return;
    }

    const lastSelectedObjectId = this.lastSelectedObjectId;
    if (lastSelectedObjectId !== undefined) {
      this.leaveFormModal()
        .pipe(
          tap((data) => {
            if (data === null) {
              this.hideForm();
            }

            if (data && !data.response) {
              this.lastSelectedObjectId = lastSelectedObjectId;
              this.confirmChangesDialogOpen = false;
              this.canvas.selectTableById(lastSelectedObjectId);
              this.canvas.detectChanges();
            }
          }),
          filter(data => data?.response),
        )
        .subscribe(({ response }) => {
          if (response) {
            this.hideForm();
            this.canvas.onFloorChange(this.selectedFloorPlanId, 'builder onCanvasClicked()');
          }
        });
    }
  }

  onObjectSelected(props: CanvasObjectSelected): void {
    const lastSelectedId = this.lastSelectedObjectId;
    this.selectedObjectId = props.id;
    this.selectedResourceId = this.duplicatedTable ? this.selectedResourceId : props.resourceId;
    this.selectedObjectSectionId = props.sectionId;
    this.left = props.left;
    this.top = props.top;
    this.selectedSize = props.size;
    this.width = props.width;
    this.height = props.height;
    this.isTable = props.isTable;
    this.selectedShape = props.shape;
    this.objectName = props.name;
    this.rotation = Number(props.rotation.toFixed(0));
    this.popoutHidden = this.rightSidebarBreakpoint ? this.selectedObjectId === undefined : true;

    const objectSource = props.isTable
      ? this.floorPlanTableService.savedTables : this.floorPlanTableService.savedObjects;
    this.isObjectEditMode = objectSource.findIndex(item => item.id === this.selectedObjectId) !== -1;
    this.openedAddTableForm = !this.selectedSectionId && props.isTable;
    this.openedAddObjectForm = !this.selectedSectionId && !props.isTable;

    if (!this.popoutHidden) {
      /*
      const { width: popoutWidth } = this.popoutEl.nativeElement.getBoundingClientRect();
      const { width: canvasWidth } = this.canvasRef.nativeElement.getBoundingClientRect();
      const popoutOffsetLeft = canvasWidth - popoutWidth;
      const tableLeft = (props.left * this.canvas.getZoom()) - this.canvas.getCurrentScrollLeft();

     if (tableLeft >= popoutOffsetLeft) {
       //const deltaX = (tableLeft - popoutOffsetLeft) + props.width + 10;
       this.canvas.canvasScrollLeftTo(10);
     }
     */
    }

    if (
      this.activeTab === FloorPlanTabs.tables &&
      this.selectedObjectId !== lastSelectedId &&
      lastSelectedId !== undefined &&
      !this.confirmChangesDialogOpen
    ) {
      const tableFormNotTouched = this.isTable && !this.tableAddFormTouched;
      const objectFormNotTouched = !this.isTable && !this.objectAddFormTouched;

      if (tableFormNotTouched || objectFormNotTouched) {
        this.openedAddTableForm = false;
        this.openedAddObjectForm = false;
        this.lastSelectedObjectId = props.id;
        setTimeout(() => {
          this.openedAddTableForm = props.isTable;
          this.openedAddObjectForm = !props.isTable;
          const tableIdx = this.floorPlanTableService.savedTables.findIndex(table => table.id === lastSelectedId);
          const objectIdx = this.floorPlanTableService.savedObjects.findIndex(item => item.id === lastSelectedId);

          if (tableIdx < 0 && objectIdx < 0) {
            this.canvas.removeObject(lastSelectedId);
          }
        });
        return;
      }

      this.confirmChangesDialogOpen = true;
      this.leaveFormModal()
        .pipe(
          tap((res) => {
            if (res && !res.response) {
              this.lastSelectedObjectId = lastSelectedId;
              this.confirmChangesDialogOpen = false;
              this.canvas.selectTableById(lastSelectedId);
              this.canvas.detectChanges();
            }
          }),
          filter(data => data?.response),
        )
        .subscribe((res) => {
          this.openedAddTableForm = false;
          this.openedAddObjectForm = false;
          setTimeout(() => {
            this.canvas.onFloorChange(this.selectedFloorPlanId, 'builder onObjectSelected', () => {
              this.lastSelectedObjectId = props.id;
              this.confirmChangesDialogOpen = false;
              this.canvas.selectTableById(props.id);
              this.canvas.detectChanges();
            });
          });
        });
    } else {
      this.lastSelectedObjectId = props.id;
    }
  }

  fromSectionsToTables(tableId: string): void {
    this.activeTab = FloorPlanTabs.tables;
    setTimeout(() => {
      this.canvas.selectTableById(tableId);
      this.canvas.detectChanges();
    });
  }

  saveTableData(dragEvent: CanvasObjectDrag): void {
    this.selectedShape = dragEvent.shape;
    this.selectedSize = dragEvent.size;
    this.selectedCanvasObject = dragEvent.objectType;
  }

  changeObjectText(name: string): void {
    this.canvas.setObjectText(this.selectedObjectId, name);
  }

  changeTableText(name?: string, capacity?: string): void {
    this.canvas.setTableTextPropName('id', this.selectedObjectId, true, false, 0, name, capacity);
  }

  changeTableSizeOrShape(size: TableSizes, shape: Shapes): void {
    this.selectedSize = SIZE_TO_TEXT[size];
    this.selectedShape = shape;

    let width = this.width;
    let height = this.height;

    if (size !== TableSizes.custom) {
      width = SHAPES_DIMENSIONS[shape][size].width;
      height = SHAPES_DIMENSIONS[shape][size].height;
    } else if (size === TableSizes.custom && shape === Shapes.square && width !== height) {
      const shortSide = width >= height ? width : height;
      width = shortSide;
      height = shortSide;
    }

    this.canvas.setObjectShape(
      this.selectedObjectId,
      this.selectedShape,
      width,
      height,
    );
  }

  changeTableRotation(rotation: number): void {
    this.canvas.setTableRotation(this.selectedObjectId, rotation);
  }

  dropElement([originalTop, originalLeft]: [number, number]): void {
    const zoom = this.canvas.getZoom();
    const top = originalTop / zoom;
    const left = originalLeft / zoom;
    const name = '';

    if (this.selectedCanvasObject === CanvasObjectsEnum.object) {
      let width = 108;
      let height = 57;

      if (this.selectedShape === Shapes.circle) {
        width = 57;
        height = 57;
      }

      this.objectName = name;
      this.canvas.addObject(
        v4(),
        name,
        width,
        height,
        top,
        left,
        this.selectedShape,
        true,
      );
    }

    if (this.selectedCanvasObject === CanvasObjectsEnum.table) {
      this.generatedTableName = `Table ${this.findAvailableTableName(this.floorPlanTablesCount + 1)}`;
      this.canvas.addTable(
        v4(),
        this.generatedTableName,
        SIZE_TO_TEXT[this.selectedSize],
        '',
        SHAPES_DIMENSIONS[this.selectedShape][this.selectedSize].width,
        SHAPES_DIMENSIONS[this.selectedShape][this.selectedSize].height,
        top,
        left,
        this.selectedShape,
        false,
        false,
        0,
        undefined,
        undefined,
        '',
        true,
      );
    }

    this.selectedResourceId = null;
  }

  findAvailableTableName(nameToCheck: number): number {
    let resourceExists = false;
    const resourceIdToTableDataMap = this.floorPlanTableService.resourceIdToTableDataMap;

    this.resources
      .forEach((res) => {
        const floorPlan = resourceIdToTableDataMap[res._id];
        if (resourceExists || floorPlan?.floorPlanId !== this.selectedFloorPlanId) {
          return;
        }

        if (res.name === `Table ${nameToCheck}`) {
          resourceExists = true;
        }
      });

    if (resourceExists) {
      return this.findAvailableTableName(nameToCheck + 1);
    }

    return nameToCheck;
  }

  autoSave(objectProps: CanvasObjectSelected): void {
    const NOT_FOUND = -1;
    if (objectProps.isTable && this.floorPlanTableService.savedTables
      .findIndex(table => table.id === objectProps.id) === NOT_FOUND
    ) {
      return;
    }

    if (!objectProps.isTable && this.floorPlanTableService.savedObjects
      .findIndex(table => table.id === objectProps.id) === NOT_FOUND
    ) {
      return;
    }

    this.onTransformUpdate(
      objectProps.id,
      objectProps.isTable ? FloorPlanItemType.table : FloorPlanItemType.object,
      {
        width: objectProps.width,
        height: objectProps.height,
        top: objectProps.top,
        left: objectProps.left,
        size: objectProps.size,
        shape: objectProps.shape,
        rotation: objectProps.rotation,
      },
      objectProps.sectionId,
    );
  }

  hideForm(popoutHidden = true): void {
    this.selectedObjectId = undefined;
    this.lastSelectedObjectId = undefined;
    this.openedAddTableForm = false;
    this.openedAddObjectForm = false;
    this.popoutHidden = popoutHidden;

    setTimeout(() => {
      this.canvas.deselectAllObjects();
    });
  }

  removeObjectFromCanvas(id: string): void {
    this.canvas.removeObject(id);
    this.selectedObjectId = null;
  }

  onChangeTab(tab: string): void {
    if (this.activeTab === FloorPlanTabs.tables && (this.openedAddTableForm || this.openedAddObjectForm)) {
      this.leaveFormModal()
        .pipe(
          filter((data) => {
            if (data === null) {
              return true;
            }

            return data?.response;
          }),
          tap(() => {
            this.activeTab = tab as FloorPlanTabs;
            this.hideForm(this.popoutHidden);
            this.canvas.onFloorChange(this.selectedFloorPlanId, 'builder onChangeTab()');
          }),
        )
        .subscribe();
    } else {
      this.activeTab = tab as FloorPlanTabs;
    }
  }

  get floorPlansCount(): number {
    return this.floorPlanTableService.floorPlans?.length || 0;
  }

  ngOnDestroy(): void {
    this.pageIdentifyService.remove(FLOORPLAN_GLOBAL_CLASSNAME);
    this.subscriptions.unsubscribe();
  }

  private onSectionCloseHandler(isCreatingNewSection: boolean, callback: () => void) {
    return (name: string, tableIds: string[], color: string, sectionId?: string) => {
      const newSection: FloorPlanNewSection = {
        name,
        color,
        tableIds,
        type: FloorPlanItemType.section,
        getAttributes(): SectionAttributes {
          return {
            color,
            tableIds,
          };
        },
      };

      const existingSection: FloorPlanSection = {
        ...newSection,
        id: sectionId,
        floorPlanId: this.selectedFloorPlanId,
        getAttributes(): SectionAttributes {
          return {
            color,
            tableIds,
          };
        },
      };

      this.allowSectionMultiSelection = false;
      this.canvas.deselectAllObjects();

      let endpoint = this.itemService.create(this.selectedFloorPlanId, newSection);
      if (!isCreatingNewSection) {
        endpoint = this.itemService.updateById(existingSection);
      }

      endpoint.pipe(
        take(1),
        delay(1),
      )
        .subscribe(() => {
          if (callback) {
            callback();
          }
        });
    };
  }

  private modalSave(
    mode: FloorPlanModalMode,
    item: FloorPlan,
    instance: FloorplanModalComponent,
    deleteResources?: boolean,
  ): void {
    let endpoint: Observable<FloorPlan | null> = this.service.create({
      name: item.name,
      default: item.default,
    });

    if (mode === FloorPlanModalMode.edit) {
      endpoint = this.service.updateById(item.id, {
        name: item.name,
        default: item.default,
      });
    }

    if (mode === FloorPlanModalMode.delete) {
      endpoint = this.service.deleteById(item.id, deleteResources);
    }

    endpoint.pipe(
      take(1),
      catchError((err) => {
        this.errorHandler.handleError(err);

        return of(false);
      }),
      filter(res => res !== false),
    )
      .subscribe(() => {
        instance.close();
      });
  }

  private leaveFormModal(): Observable<ConfirmationModalResult<boolean>> {
    if (this.isObjectEditMode && !this.tableAddFormTouched && !this.objectAddFormTouched) {
      return of(null);
    }

    return this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(
      ConfirmationModalComponent,
      {
        options: {
          content: 'Leave the form? Changes that you made will not be saved.',
          title: `Cancel  ${this.isTable ? 'table' : 'object'} changes`,
          closeBtnText: 'Cancel',
        },
      },
      FrontendModalSize.default,
      false,
    )
      .pipe(take(1));
  }
}
